import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { auth, db } from "../firebase/firebaseConfig"; // Import auth and db from the initialized Firebase config
import { FirestoreContextProvider } from "../firebase/FirestoreContext"; // Import the FirestoreContextProvider
import ShopPage from "../screens/app/ShopPage";
import Homepage from "../screens/app/Homepage";
import SignUp from "../screens/auth/SignUp";
import SignIn from "../screens/auth/SignIn";
import Listing from "../screens/app/Listing";
import Cart from "../screens/app/Cart";
import CartLoader from "../screens/app/CartLoader";
import Checkout from "../screens/app/Checkout";
import CheckoutSquare from "../screens/app/CheckoutSquare";
import CheckoutRouter from "../screens/app/CheckoutRouter";
import UserAccount from "../screens/app/UserAccount";
import Help from "../screens/app/Help";
import HelpCenter from "../screens/app/HelpCenter";
import Support from "../screens/app/Support";
import Share from "../screens/app/Share";
import Legal from "../screens/app/Legal";
import UsersAddresses from "../screens/app/UserAddresses";
import OrderHistory from "../screens/app/OrderHistory";
import PaymentSuccess from "../screens/app/PaymentSuccess";
import PaymentRequest from "../screens/app/PaymentRequest";
import FAQ from "../screens/app/FAQ";
import { onAuthStateChanged } from "firebase/auth";
import NewsletterUnsubscribe from "../screens/app/NewsletterUnsubscribe";
import ForgottenPass from "../screens/auth/ForgottenPass";
import CategoryPage from "../screens/app/CategoryPage";
import CategoryCustomPage from "../screens/app/CategoryCustomPage";
import ProductRequest from "../screens/app/ProductRequest";
import AboutUs from "../screens/app/AboutUs";
import Knowledge from "../screens/app/Knowledge";
import Wallet from "../screens/app/Wallet";
import Favorites from "../screens/app/Favorites";
import ReviewCreate from "../screens/app/ReviewCreate";
import Reviews from "../screens/app/Reviews";
import FDADisclaimer from "../screens/app/FDADisclaimer";
import PrivacyPolicy from "../screens/app/PrivacyPolicy";
import TermsOfService from "../screens/app/TermsOfService";
import CertificatesOfAnalysis from "../screens/app/CertificatesOfAnalysis";
import ConfirmationCode from "../screens/auth/ConfirmationCode";
import SearchPage from "../screens/app/SearchPage";
import Rewards from "../screens/app/Rewards";

import CustomModals from "../elements/CustomModals";
import NotFound from "../screens/app/NotFound";

const AppRouter = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    // Clean up the Auth listener when the component unmounts
    return () => unsubscribeAuth();
  }, []);

  if (loading) {
    // Show a loading indicator while waiting for auth state
    return <div className="loading">Loading...</div>;
  }

  return (
    <FirestoreContextProvider db={db}>
      {/* Wrap the Router with FirestoreContextProvider */}

      <CustomModals />
      <Routes>
        {/* Main shop page */}
        <Route path="/" element={<Homepage />} />
        {/* Mail confirmation page */}
        <Route path="/c/:confirmationCode" element={<ConfirmationCode />} />
        {/* Shop */}
        <Route path="/shop" element={<ShopPage />} />
        {/* Sign in page */}
        <Route path="/signin" element={<SignIn />} />
        {/* Sign Up page */}
        <Route path="/signup" element={<SignUp />} />
        {/* Search page */}
        <Route path="/search/:searchTerm" element={<SearchPage />} />
        {/* Listing detail page with dynamic listingID */}
        <Route path="/listing/:listingID" element={<Listing />} />
        {/* Listing detail page with dynamic listingID */}
        <Route path="/product-category/:catName" element={<CategoryPage />} />
        {/* Custom category page with dynamic category name */}
        <Route path="/custom-category/:name" element={<CategoryCustomPage />} />
        {/* Review page */}
        <Route path="/review-create/:productID" element={<ReviewCreate />} />
        <Route path="/reviews/:productID" element={<Reviews />} />
        {/* Cart page */}
        <Route path="/cart" element={<Cart />} />
        {/* Cart loader page with base64 data */}
        <Route path="/cart/:base64Data" element={<CartLoader />} />
        {/* Checkout page - routes to appropriate payment platform */}
        <Route path="/checkout" element={<CheckoutRouter />} />
        {/* Direct checkout routes for specific platforms */}
        <Route path="/checkout-stripe" element={<Checkout />} />
        <Route path="/checkout-square" element={<CheckoutSquare />} />
        {/* User account page */}
        <Route path="/useraccount" element={user?.email ? <UserAccount /> : <Navigate to="/signin" replace />} />
        {/* Help page */}
        <Route path="/help" element={<Help />} />
        <Route path="/help-center" element={<HelpCenter />} />
        <Route path="/support" element={<Support />} />
        {/* Legal page */}
        <Route path="/legal" element={<Legal />} />
        {/* Saved addresses page */}
        <Route path="/useraddresses" element={<UsersAddresses />} />
        {/* Order history page */}
        <Route path="/orderhistory" element={user ? <OrderHistory /> : <Navigate to="/signin" replace />} />
        {/* Payment success page */}
        <Route path="/payment-success" element={<PaymentSuccess />} />
        {/* Payment request page (manual Zelle/Venmo flow) */}
        <Route path="/payment-request" element={<PaymentRequest />} />
        {/* Newsletter unsubscribe page */}
        <Route path="/newsletter-unsubscribe" element={<NewsletterUnsubscribe />} />
        {/* FAQ page */}
        <Route path="/faq" element={<FAQ />} />
        {/* Share page */}
        <Route path="/share" element={<Share />} />
        {/* Forgotten password page */}
        <Route path="/forgotten-pass" element={<ForgottenPass />} />
        <Route path="/r" element={<ForgottenPass />} />
        {/* Product request page */}
        <Route path="/product-request" element={<ProductRequest />} />
        {/* About us page */}
        <Route path="/about-us" element={<AboutUs />} />
        {/* Knowledge page */}
        <Route path="/knowledge" element={<Knowledge />}>
          <Route path=":id" element={<Knowledge />} />
        </Route>
        {/* Wallet page */}
        <Route path="/wallet" element={<Wallet />} />
        {/* Favorites page */}
        <Route path="/favorites" element={<Favorites />} />
        {/* Rewards page */}
        <Route path="/rewards" element={<Rewards />} />
        {/* Legal pages */}
        <Route path="/legal/fda-disclaimer" element={<FDADisclaimer />} />
        <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/legal/terms-of-service" element={<TermsOfService />} />
        <Route path="/legal/certificates-of-analysis" element={<CertificatesOfAnalysis />} />
        {/* Catch-all route for 404 Not Found */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </FirestoreContextProvider>
  );
};

export default AppRouter;
